type Route = {
    path: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    alwaysAllowed: boolean;
};

type Routes = {
    activityLogs: {
        fetchByEntity: Route;
        fetchById: Route;
        fetchAll: Route;
        fetchAutoProvisions: Route;
        cancelAutoProvision: Route;
    },
    ai: {
        classifyBusinessType: Route;
    },
    bank: {
        fetch: Route;
        fetchById: Route;
        create: Route;
        update: Route;
        delete: Route; 
        togglePresentational: Route;
        toggleOperational: Route;
    },
    basiq: {
        connectUser: Route;
        getTransactions: Route;
        createUser: Route;
        checkUser: Route;
    },
    categoryProviders: {
        fetch: Route;
        fetchById: Route;
        create: Route;
        update: Route;
        delete: Route;
    },
    bankAccounts: {
        fetchById: Route;
        fetchByEntity: Route;
        create: Route;
        update: Route;
        delete: Route;
        toggleStatus: Route;
    },
    businessSetup: {
        fetchTypes: Route;
        set: Route;
    },
    categories: {
        fetchById: Route;
        fetchByEntity: Route;
        create: Route;
        import: Route;
        update: Route;
        delete: Route;
        toggleStatus: Route;
        checkDuplicate: Route;
        createImportEvent: Route;
        reassign: Route;
    },
    client: {
        fetchById: Route;
        fetchByEntity: Route;
        invoiceStats: Route;
        invoiceWindow: Route;
        create: Route;
        update: Route;
        delete: Route;
        import: Route;
        createImportEvent: Route;
        checkDuplicate: Route;
        paymentFrequency: Route;
        paymentFrequencyByEmail: Route;
        recomputeProvisionDate: Route;
        analyzeSearchString: Route;
        analyzeAllSearchStrings: Route;
    },
    company: {
        fetch: Route;
        fetchById: Route;
        create: Route;
        update: Route;
        delete: Route;
        toggleEnabled: Route;
        toggleAccess: Route;
        loginNotification: Route;
        hardDelete: Route;
    };
    companySettings: {
        fetch: Route;
        fetchById: Route;
        update: Route;
        delete: Route;
    };
    contributions: {
        fetchById: Route;
        create: Route;
        update: Route;
        delete: Route;
        fetchByEntity: Route;
        toggleStatus: Route;
    },
    country: {
        fetch: Route;
        create: Route;
        update: Route;
        delete: Route;
    };
    filter: {
        fetchByGuid: Route;
        save: Route;
        saveFavorites: Route;
    },
    impersonate: {
        start: Route;
        stop: Route;
        unlock: Route;
        companyUsers: Route;
    },
    imports: {
        fetchById: Route;
        fetchByEntity: Route;
        delete: Route;
        restore: Route;
        requestHardDelete: Route;
    },
    invoices: {
        fetchById: Route;
        fetchByEntity: Route;
        create: Route;
        update: Route;
        delete: Route;
        import: Route;
        createImportEvent: Route;
        checkDuplicate: Route;
        resendEmail: Route;
        downloadPdf: Route;
        recalculateProvisions: Route;
    },
    invoiceSettings: {
        createOrUpdate: Route;
        fetchByEntity: Route;
        createStatus: Route;
        fetchStatusById: Route;
        updateStatus: Route;
        deleteStatus: Route;
        fetchStatusesByEntity: Route;
    },
    invoiceProviders: {
        fetch: Route;
        fetchById: Route;
        create: Route;
        update: Route;
        delete: Route;
    },
    newsletter: {
        subscribe: Route;
    },
    otp: {
        send: Route;
        verify: Route;
    },
    provisionTemplate: {
        fetchById: Route;
        fetchByEntity: Route;
        fetchTransactionsById: Route;
        create: Route;
        update: Route;
        delete: Route;
        deleteFutureOccurrences: Route;
        deleteAllOccurrences: Route;
        occurrenceHistory: Route;
        terminate: Route;
        group: Route;
        ungroup: Route;
        removeGroupMembers: Route;
        merge: Route;
        analyzePattern: Route;
        analyzePatternByDescription: Route;
        analyzePatternByAI: Route;
        analyzeMultiple: Route;
        markTransactionAsProvisioned: Route;
        autoProvision: Route;
    },
    simulation: {
        fetchById: Route;
        fetchByEntity: Route;
        create: Route;
        update: Route;
        delete: Route;
        fetchDetailById: Route;
    },
    staff: {
        fetchById: Route;
        create: Route;
        update: Route;
        delete: Route;
        fetchByEntity: Route;
        import: Route;
    },
    taxDefinitions: {
        fetch: Route;
        fetchById: Route;
        create: Route;
        update: Route;
        delete: Route;
        permanentDelete: Route;
        fetchByEntity: Route;
        toggleStatus: Route;
        toggleEnabled: Route;
        toggleShowInTransactions: Route;
    },
    transaction: {
        create: Route;
        fetchByEntity: Route;
        fetchRelated: Route;
        fetchXeroPaymentCandidates: Route;
        fetchRealCandidatesForPayment: Route;
        autoReconcileXeroPayments: Route;
        bulkCreate: Route;
        fetchByGuid: Route;
        updateCategory: Route;
        updateTax: Route;
        updateDate: Route;
        link: Route;
        unlink: Route;
        delete: Route;
        // Edit/delete for manually-added transactions only (the ones created
        // via the "+ Transaction" button); the server rejects anything else.
        updateManual: Route;
        deleteManual: Route;
        toggleLinkable: Route;
        checkDuplicate: Route;
        createImportEvent: Route;
        summary: Route;
        // getCashflowStatus: Route; // SUPERSEDED - computed client-side now, see util/transaction-calculations.ts
    };
    user: {
        login: Route;
        register: Route;
        oauth: Route;
        acceptAgreement: Route;
        forgotPassword: Route;
        checkResetToken: Route;
        resetPassword: Route;
        checkEnrollment: Route;
        fetchProfile: Route;
        updateProfile: Route;
        logout: Route;
        sessionHeartbeat: Route;
    };
    userSettings: {
        fetchById: Route;
        fetchByEntity: Route;
        fetchAdmins: Route;
        create: Route;
        update: Route;
        delete: Route;
        checkInvitation: Route;
        updateRole: Route;
    };
    documents: {
        byEntity: Route;
        upload: Route;
        byInvoice: Route;
        fromInvoice: Route;
        fromWebpage: Route;
        manage: Route;
        analytics: Route;
        ownerFile: Route;
        createLink: Route;
        updateLink: Route;
        binByEntity: Route;
        restore: Route;
        purgeBin: Route;
        emptyBin: Route;
        snapshot: Route;
        update: Route;
        delete: Route;
        regenerateLink: Route;
        addViewer: Route;
        updateViewer: Route;
        removeViewer: Route;
        access: Route;
        requestAccess: Route;
        track: Route;
        endTrack: Route;
        pay: Route;
        confirmPayment: Route;
    };
    documentFolders: {
        byEntity: Route;
        create: Route;
        update: Route;
        delete: Route;
        share: Route;
        updateShare: Route;
        unshare: Route;
        regenerateShare: Route;
        manage: Route;
        addViewer: Route;
        updateViewer: Route;
        removeViewer: Route;
        shareAccess: Route;
        requestAccess: Route;
        contents: Route;
    };
    stripe: {
        get: Route;
        save: Route;
        remove: Route;
        sync: Route;
        syncStatus: Route;
    };
    entityRoles: {
        fetchByEntity: Route;
        create: Route;
        update: Route;
        delete: Route;
        updatePermissions: Route;
        assignRole: Route;
        myPermissions: Route;
    };
    features: {
        getByEntity: Route;
        getAll: Route;
        upsert: Route;
        delete: Route;
    };
    gstReportingCycles: {
        fetchByEntity: Route;
        save: Route;
    };
    gstFieldMappings: {
        fetchByEntity: Route;
        save: Route;
    };
    forecastGroups: {
        fetchByEntity: Route;
        createMajorHeading: Route;
        updateMajorHeading: Route;
        deleteMajorHeading: Route;
        createSubHeading: Route;
        updateSubHeading: Route;
        deleteSubHeading: Route;
        addSubGroups: Route;
        addSubGroupsToSubHeading: Route;
        updateSubGroup: Route;
        deleteSubGroup: Route;
    };
    colorScheme: {
        fetch: Route;
        save: Route;
    };
    provisionAging: {
        fetchStatus: Route;
        trigger: Route;
    };
    generalProvisions: {
        fetchByEntity: Route;
        fetchTransactions: Route;
        update: Route;
        setStatus: Route;
    };
    subscription: {
        fetchStatus: Route;
        select: Route;
        acknowledgeUpgrade: Route;
        toggleAutoRenew: Route;
        setAddon: Route;
        billingHistory: Route;
        featureGate: Route;
        adminSet: Route;
        stripeInitiate: Route;
        stripeChangePlan: Route;
        stripeUpgradeForFeature: Route;
        stripeSync: Route;
        overdue: Route;
        payOverdue: Route;
        qaDailyPlan: Route;
        setQaDailyPlan: Route;
        platformStripeKey: Route;
    };
    subscriptionTiers: {
        list: Route;
        update: Route;
        reset: Route;
        create: Route;
        delete: Route;
        addons: Route;
        updateAddon: Route;
        createAddon: Route;
        deleteAddon: Route;
    };
    subscriptionReimbursements: {
        list: Route;
        transactions: Route;
        resolve: Route;
    };
    xero: {
        getIntegration: Route;
        connect: Route;
        refreshConnectLink: Route;
        disconnect: Route;
        syncGL: Route;
        syncTransactions: Route;
        syncInvoices: Route;
        syncStale: Route;
        syncStatuses: Route;
        syncClients: Route;
        getStatus: Route;
        syncHistory: Route;
        importStatus: Route;
        invoicesBeyondHorizon: Route;
        continueInvoiceImport: Route;
        getGLAccounts: Route;
    };
};

const routes: Routes = {
    activityLogs: {
        fetchByEntity: {
            path: '/activity-logs/entity/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchById: {
            path: '/activity-logs/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchAll: {
            path: '/activity-logs',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchAutoProvisions: {
            path: '/activity-logs/auto-provisions',
            method: 'GET',
            alwaysAllowed: false,
        },
        cancelAutoProvision: {
            path: '/activity-logs/cancel-auto-provision/{entityId}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
    },
    ai: {
        classifyBusinessType: {
            path: '/ai/classify-business-type',
            method: 'POST',
            alwaysAllowed: false,
        }
    },
    bankAccounts: {
        fetchById: {
            path: '/bank-accounts/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchByEntity: {
            path: '/bank-accounts/entity/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        create: {
            path: '/bank-accounts/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/bank-accounts/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        delete: {
            path: '/bank-accounts/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        toggleStatus: {
            path: '/bank-accounts/toggle-status/{id}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
    },
    basiq: {
        connectUser: {
            path: '/basiq/users/{basiqUserId}/connect',
            method: 'GET',
            alwaysAllowed: false,
        },
        createUser: {
            path: '/basiq/users',
            method: 'POST',
            alwaysAllowed: false,
        },
        getTransactions: {
            path: '/basiq/transactions',
            method: 'POST',
            alwaysAllowed: false,
        },
        checkUser: {
            path: '/basiq/integrations/{userGuid}',
            method: 'GET',
            alwaysAllowed: false,
        },
    },
    bank: {
        fetch: {
            path: '/banks',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchById: {
            path: '/banks/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        create: {
            path: '/banks/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/banks/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        delete: {
            path: '/banks/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        togglePresentational: {
            path: '/banks/toggle-presentational/{id}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
        toggleOperational: {
            path: '/banks/toggle-operational/{id}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
    },
    categoryProviders: {
        fetch: {
            path: '/category-providers',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchById: {
            path: '/category-providers/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        create: {
            path: '/category-providers/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/category-providers/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        delete: {
            path: '/category-providers/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
    },
    businessSetup: {
        fetchTypes: {
            path: '/business-setup/types',
            method: 'GET',
            alwaysAllowed: false,
        },
        set: {
            path: '/business-setup/setup',
            method: 'POST',
            alwaysAllowed: false,
        },
    },
    categories: {
        fetchById: {
            path: '/categories/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchByEntity: {
            path: '/categories/entity/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        create: {
            path: '/categories/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        import: {
            path: '/categories/bulk-create',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/categories/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        delete: {
            path: '/categories/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        toggleStatus: {
            path: '/categories/toggle-status/{id}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
        checkDuplicate: {
            path: '/categories/check-duplicates/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        createImportEvent: {
            path: '/categories/create-import-event',
            method: 'POST',
            alwaysAllowed: false,
        },
        reassign: {
            path: '/categories/bulk-update',
            method: 'PUT',
            alwaysAllowed: false,
        },
    },
    client: {
        fetchById: {
            path: '/clients/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchByEntity: {
            path: '/clients/entity/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        invoiceStats: {
            path: '/clients/invoice-stats/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        // What the 12-month window counts back from (last Xero invoice sync).
        invoiceWindow: {
            path: '/clients/invoice-window/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        create: {
            path: '/clients/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/clients/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        delete: {
            path: '/clients/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        import: {
            path: '/clients/bulk-create',
            method: 'POST',
            alwaysAllowed: false,
        },
        createImportEvent: {
            path: '/clients/create-import-event',
            method: 'POST',
            alwaysAllowed: false,
        },
        checkDuplicate: {
            path: '/clients/check-duplicates/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        paymentFrequency: {
            path: '/clients/payment-frequency/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        paymentFrequencyByEmail: {
            path: '/clients/payment-frequency/by-email',
            method: 'GET',
            alwaysAllowed: false,
        },
        recomputeProvisionDate: {
            path: '/clients/recompute-provision-date/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        analyzeSearchString: {
            path: '/clients/{id}/analyze-search-string',
            method: 'GET',
            alwaysAllowed: false,
        },
        analyzeAllSearchStrings: {
            path: '/clients/entity/{entityId}/analyze-search-strings',
            method: 'POST',
            alwaysAllowed: false,
        },
    },
    contributions: {
        fetchById: {
            path: '/contributions/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        create: {
            path: '/contributions/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/contributions/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        delete: {
            path: '/contributions/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        fetchByEntity: {
            path: '/contributions/entity/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        toggleStatus: {
            path: '/contributions/toggle-status/{id}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
    },
    filter: {
        fetchByGuid: {
            path: '/filters/{userGuid}',
            method: 'GET',
            alwaysAllowed: false,
        },
        save: {
            path: '/filters/save/{userGuid}',
            method: 'POST',
            alwaysAllowed: false,
        },
        saveFavorites: {
            path: '/filters/favorite/{userGuid}',
            method: 'POST',
            alwaysAllowed: false,
        },
    },
    impersonate: {
        start: {
            path: '/impersonate/start',
            method: 'POST',
            alwaysAllowed: false,
        },
        stop: {
            path: '/impersonate/stop',
            method: 'POST',
            alwaysAllowed: false,
        },
        unlock: {
            path: '/impersonate/unlock',
            method: 'POST',
            alwaysAllowed: false,
        },
        companyUsers: {
            path: '/impersonate/entity/{entityId}/users',
            method: 'GET',
            alwaysAllowed: false,
        },
    },
    imports: {
        fetchById: {
            path: '/import-events/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchByEntity: {
            path: '/import-events/entity/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        delete: {
            path: '/import-events/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        restore: {
            path: '/import-events/restore/{id}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
        requestHardDelete: {
            path: '/import-events/request-hard-delete/{id}',
            method: 'POST',
            alwaysAllowed: false,
        },
    },
    invoices: {
        fetchById: {
            path: '/invoices/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchByEntity: {
            path: '/invoices/entity/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        create: {
            path: '/invoices/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/invoices/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        delete: {
            path: '/invoices/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        import: {
            path: '/invoices/bulk-create',
            method: 'POST',
            alwaysAllowed: false,
        },
        createImportEvent: {
            path: '/invoices/create-import-event',
            method: 'POST',
            alwaysAllowed: false,
        },
        checkDuplicate: {
            path: '/invoices/check-duplicates/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        resendEmail: {
            path: '/invoices/resend-email/{id}',
            method: 'POST',
            alwaysAllowed: false,
        },
        downloadPdf: {
            path: '/invoices/pdf/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        recalculateProvisions: {
            path: '/invoices/entity/{entityId}/recalculate-provisions',
            method: 'POST',
            alwaysAllowed: false,
        },
    },
    invoiceSettings: {
        createOrUpdate: {
            path: '/invoice-settings/settings/{entityId}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        fetchByEntity: {
            path: '/invoice-settings/settings/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        createStatus: {
            path: '/invoice-settings/status/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        fetchStatusById: {
            path: '/invoice-settings/status/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        updateStatus: {
            path: '/invoice-settings/status/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        deleteStatus: {
            path: '/invoice-settings/status/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        fetchStatusesByEntity: {
            path: '/invoice-settings/status/entity/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
    },
    invoiceProviders: {
        fetch: {
            path: '/invoice-providers',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchById: {
            path: '/invoice-providers/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        create: {
            path: '/invoice-providers/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/invoice-providers/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        delete: {
            path: '/invoice-providers/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
    },
    simulation: {
        fetchById: {
            path: '/simulations/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchByEntity: {
            path: '/simulations/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        create: {
            path: '/simulations/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/simulations/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        delete: {
            path: '/simulations/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        fetchDetailById: {
            path: '/simulations/simulated-transaction/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
    },
    staff: {
        fetchById: {
            path: '/staff/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        create: {
            path: '/staff/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/staff/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        delete: {
            path: '/staff/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        import: {
            path: '/staff/bulk-create',
            method: 'POST',
            alwaysAllowed: false,
        },
        fetchByEntity: {
            path: '/staff/entity/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
    },
    transaction: {
        create: {
            path: '/transactions/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        fetchByEntity: {
            path: '/transactions/entity/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        fetchRelated: {
            path: '/transactions/related/{guid}',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchXeroPaymentCandidates: {
            path: '/transactions/xero-payment-candidates/{guid}',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchRealCandidatesForPayment: {
            path: '/transactions/real-candidates-for-payment/{guid}',
            method: 'GET',
            alwaysAllowed: false,
        },
        autoReconcileXeroPayments: {
            path: '/transactions/auto-reconcile-xero-payments/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        fetchByGuid: {
            path: '/transactions/{guid}',
            method: 'GET',
            alwaysAllowed: false,
        },
        updateCategory: {
            path: '/transactions/update-category/{guid}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        updateTax: {
            path: '/transactions/update-tax/{guid}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        updateDate: {
            path: '/transactions/update-date/{guid}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        bulkCreate: {
            path: '/transactions/bulk-create',
            method: 'POST',
            alwaysAllowed: false,
        },
        link: {
            path: '/transactions/link',
            method: 'PUT',
            alwaysAllowed: false,
        },
        unlink: {
            path: '/transactions/unlink/{guid}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        delete: {
            path: '/transactions/delete/{guid}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        updateManual: {
            path: '/transactions/manual/{guid}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        deleteManual: {
            path: '/transactions/manual/{guid}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        toggleLinkable: {
            path: '/transactions/toggle-linkable/{guid}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        checkDuplicate: {
            path: '/transactions/check-duplicates/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        createImportEvent: {
            path: '/transactions/create-import-event',
            method: 'POST',
            alwaysAllowed: false,
        },
        summary: {
            path: '/transactions/summary/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        // getCashflowStatus: { // SUPERSEDED - computed client-side now, see util/transaction-calculations.ts
        //     path: '/transactions/entity/cash-flow-status/{entityId}',
        //     method: 'POST',
        //     alwaysAllowed: false,
        // },
    },
    country: {
        fetch: {
            path: '/countries',
            method: 'GET',
            alwaysAllowed: false,
        },
        create: {
            path: '/countries/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/countries/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        delete: {
            path: '/countries/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
    },
    company: {
        fetch: {
            path: '/entities',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchById: {
            path: '/entities/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        create: {
            path: '/entities/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/entities/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        delete: {
            path: '/entities/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        toggleEnabled: {
            path: '/entities/toggle-enabled/{id}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
        toggleAccess: {
            path: '/entities/toggle-login/{id}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
        loginNotification: {
            path: '/entities/login-notification/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        hardDelete: {
            path: '/entities/hard-delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
    },
    companySettings: {
        fetch: {
            path: '/entity-settings',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchById: {
            path: '/entity-settings/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        update: {
            path: '/entity-settings/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        delete: {
            path: '/entity-settings/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
    },
    newsletter: {
        subscribe: {
            path: '/newsletter/subscribe',
            method: 'POST',
            alwaysAllowed: false,
        },
    },
    otp: {
        send: {
            path: '/otp/send',
            method: 'POST',
            alwaysAllowed: true,
        },
        verify: {
            path: '/otp/verify',
            method: 'POST',
            alwaysAllowed: true,
        },
    },
    provisionTemplate: {
        fetchById: {
            path: '/provision-templates/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchByEntity: {
            path: '/provision-templates/entity/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchTransactionsById: {
            path: '/provision-templates/transactions/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        create: {
            path: '/provision-templates/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/provision-templates/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        delete: {
            path: '/provision-templates/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        deleteFutureOccurrences: {
            path: '/provision-templates/delete-future-occurrences/{guid}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        deleteAllOccurrences: {
            path: '/provision-templates/delete-all-occurrences/{guid}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        occurrenceHistory: {
            path: '/provision-templates/{id}/occurrence-history',
            method: 'GET',
            alwaysAllowed: false,
        },
        terminate: {
            path: '/provision-templates/terminate/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        group: {
            path: '/provision-templates/group',
            method: 'PUT',
            alwaysAllowed: false,
        },
        ungroup: {
            path: '/provision-templates/ungroup',
            method: 'PUT',
            alwaysAllowed: false,
        },
        removeGroupMembers: {
            path: '/provision-templates/group/remove-members',
            method: 'PUT',
            alwaysAllowed: false,
        },
        merge: {
            path: '/provision-templates/merge',
            method: 'POST',
            alwaysAllowed: false,
        },
        analyzePattern: {
            path: '/provision-templates/analyze-patterns/{transactionGuid}',
            method: 'GET',
            alwaysAllowed: false,
        },
        analyzePatternByDescription: {
            path: '/provision-templates/analyze-patterns-by-description/{transactionGuid}',
            method: 'GET',
            alwaysAllowed: false,
        },
        analyzePatternByAI: {
            path: '/provision-templates/analyze-patterns-by-ai/{transactionGuid}',
            method: 'GET',
            alwaysAllowed: false,
        },
        analyzeMultiple: {
            path: '/provision-templates/analyze-multiple-patterns',
            method: 'POST',
            alwaysAllowed: false,
        },
        markTransactionAsProvisioned: {
            path: '/provision-templates/mark-transactions/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        autoProvision: {
            path: '/provision-templates/auto-provision', // requires entityId as query or body parameter
            method: 'POST',
            alwaysAllowed: false,
        },
    },
    taxDefinitions: {
        fetch: {
            path: '/tax-definitions',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchById: {
            path: '/tax-definitions/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        create: {
            path: '/tax-definitions/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/tax-definitions/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        delete: {
            path: '/tax-definitions/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        permanentDelete: {
            path: '/tax-definitions/permanent/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        fetchByEntity: {
            path: '/tax-definitions/entity/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        toggleStatus: {
            path: '/tax-definitions/toggle-status/{id}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
        toggleEnabled: {
            path: '/tax-definitions/toggle-enabled/{id}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
        toggleShowInTransactions: {
            path: '/tax-definitions/toggle-show-in-transactions/{id}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
    },
    user: {
        login: {
            path: '/users/login',
            method: 'POST',
            alwaysAllowed: true,
        },
        register: {
            path: '/users/register',
            method: 'POST',
            alwaysAllowed: true,
        },
        oauth: {
            path: '/users/oauth-login',
            method: 'POST',
            alwaysAllowed: true,
        },
        acceptAgreement: {
            path: '/agreements/accept',
            method: 'POST',
            alwaysAllowed: true,
        },
        forgotPassword: {
            path: '/users/forgot-password',
            method: 'POST',
            alwaysAllowed: true,
        },
        checkResetToken: {
            path: '/users/reset-password/{token}',
            method: 'GET',
            alwaysAllowed: true,
        },
        resetPassword: {
            path: '/users/reset-password',
            method: 'POST',
            alwaysAllowed: true,
        },
        // The logged-in user's own profile - every user can view/edit theirs.
        fetchProfile: {
            path: '/users/me',
            method: 'GET',
            alwaysAllowed: true,
        },
        updateProfile: {
            path: '/users/me',
            method: 'PUT',
            alwaysAllowed: true,
        },
        // Server-side session (express-server session-service.js): logout
        // ends it; the heartbeat keeps it alive while the user is active.
        logout: {
            path: '/users/logout',
            method: 'POST',
            alwaysAllowed: true,
        },
        sessionHeartbeat: {
            path: '/users/session/heartbeat',
            method: 'POST',
            alwaysAllowed: true,
        },
        checkEnrollment: {
            path: '/users/enrollment/{token}',
            method: 'GET',
            alwaysAllowed: true,
        }
    },
    userSettings: {
        fetchById: {
            path: '/entity-users/{id}',
            method: 'GET',
            alwaysAllowed: false,
        },
        fetchByEntity: {
            path: '/entity-users/entity/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        // The company's admins (name + email) - any member can read this.
        fetchAdmins: {
            path: '/entity-users/entity/{entityId}/admins',
            method: 'GET',
            alwaysAllowed: true,
        },
        create: {
            path: '/entity-users/create',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/entity-users/update/{id}',
            method: 'PUT',
            alwaysAllowed: false,
        },
        updateRole: {
            path: '/entity-users/update-role/{id}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
        delete: {
            path: '/entity-users/delete/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        checkInvitation: {
            path: '/entity-users/invitation/{token}',
            method: 'GET',
            alwaysAllowed: false,
        }
    },
    documents: {
        byEntity: {
            path: '/documents/by-entity/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        upload: {
            path: '/documents',
            method: 'POST',
            alwaysAllowed: false,
        },
        byInvoice: {
            path: '/documents/by-invoice/{invoiceId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        fromInvoice: {
            path: '/documents/from-invoice/{invoiceId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        fromWebpage: {
            path: '/documents/from-webpage',
            method: 'POST',
            alwaysAllowed: false,
        },
        manage: {
            path: '/documents/{slug}/manage',
            method: 'GET',
            alwaysAllowed: false,
        },
        analytics: {
            path: '/documents/{slug}/analytics',
            method: 'GET',
            alwaysAllowed: false,
        },
        ownerFile: {
            path: '/documents/{slug}/owner-file',
            method: 'GET',
            alwaysAllowed: false,
        },
        createLink: {
            path: '/documents/{slug}/links',
            method: 'POST',
            alwaysAllowed: false,
        },
        updateLink: {
            path: '/documents/{slug}/links/{linkId}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
        binByEntity: {
            path: '/documents/bin/by-entity/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        restore: {
            path: '/documents/{slug}/restore',
            method: 'POST',
            alwaysAllowed: false,
        },
        purgeBin: {
            path: '/documents/bin/{slug}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        emptyBin: {
            path: '/documents/bin/empty',
            method: 'POST',
            alwaysAllowed: false,
        },
        snapshot: {
            path: '/documents/{slug}/snapshot',
            method: 'GET',
            alwaysAllowed: true,
        },
        update: {
            path: '/documents/{slug}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
        delete: {
            path: '/documents/{slug}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        regenerateLink: {
            path: '/documents/{slug}/regenerate',
            method: 'POST',
            alwaysAllowed: false,
        },
        addViewer: {
            path: '/documents/{slug}/viewers',
            method: 'POST',
            alwaysAllowed: false,
        },
        updateViewer: {
            path: '/documents/{slug}/viewers/{id}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
        removeViewer: {
            path: '/documents/{slug}/viewers/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        access: {
            path: '/documents/{slug}/access',
            method: 'GET',
            alwaysAllowed: true,
        },
        requestAccess: {
            path: '/documents/{slug}/request-access',
            method: 'POST',
            alwaysAllowed: true,
        },
        track: {
            path: '/documents/{slug}/track',
            method: 'POST',
            alwaysAllowed: true,
        },
        endTrack: {
            path: '/documents/{slug}/track/{trackId}',
            method: 'PATCH',
            alwaysAllowed: true,
        },
        pay: {
            path: '/documents/{slug}/pay',
            method: 'POST',
            alwaysAllowed: true,
        },
        confirmPayment: {
            path: '/documents/{slug}/confirm-payment',
            method: 'POST',
            alwaysAllowed: true,
        },
    },
    documentFolders: {
        byEntity: {
            path: '/document-folders/by-entity/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        create: {
            path: '/document-folders',
            method: 'POST',
            alwaysAllowed: false,
        },
        update: {
            path: '/document-folders/{id}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
        delete: {
            path: '/document-folders/{id}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        share: {
            path: '/document-folders/{id}/share',
            method: 'POST',
            alwaysAllowed: false,
        },
        updateShare: {
            path: '/document-folders/{id}/share',
            method: 'PATCH',
            alwaysAllowed: false,
        },
        unshare: {
            path: '/document-folders/{id}/share',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        regenerateShare: {
            path: '/document-folders/{id}/share/regenerate',
            method: 'POST',
            alwaysAllowed: false,
        },
        manage: {
            path: '/document-folders/{id}/manage',
            method: 'GET',
            alwaysAllowed: false,
        },
        addViewer: {
            path: '/document-folders/{id}/viewers',
            method: 'POST',
            alwaysAllowed: false,
        },
        updateViewer: {
            path: '/document-folders/{id}/viewers/{viewerId}',
            method: 'PATCH',
            alwaysAllowed: false,
        },
        removeViewer: {
            path: '/document-folders/{id}/viewers/{viewerId}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        shareAccess: {
            path: '/document-folders/by-slug/{slug}/access',
            method: 'GET',
            alwaysAllowed: true,
        },
        requestAccess: {
            path: '/document-folders/by-slug/{slug}/request-access',
            method: 'POST',
            alwaysAllowed: true,
        },
        contents: {
            path: '/document-folders/by-slug/{slug}/contents',
            method: 'GET',
            alwaysAllowed: true,
        },
    },
    entityRoles: {
        fetchByEntity: { path: '/entity-roles/entity/{entityId}', method: 'GET', alwaysAllowed: false },
        create: { path: '/entity-roles/create', method: 'POST', alwaysAllowed: false },
        update: { path: '/entity-roles/update/{id}', method: 'PUT', alwaysAllowed: false },
        delete: { path: '/entity-roles/delete/{id}', method: 'DELETE', alwaysAllowed: false },
        updatePermissions: { path: '/entity-roles/{id}/permissions', method: 'PUT', alwaysAllowed: false },
        assignRole: { path: '/entity-roles/assign', method: 'PATCH', alwaysAllowed: false },
        myPermissions: { path: '/entity-roles/my-permissions/{entityId}', method: 'GET', alwaysAllowed: false },
    },
    features: {
        getByEntity: { path: '/features/{entityId}', method: 'GET', alwaysAllowed: false },
        getAll:      { path: '/features', method: 'GET', alwaysAllowed: false },
        upsert:      { path: '/features', method: 'POST', alwaysAllowed: false },
        delete:      { path: '/features/{id}', method: 'DELETE', alwaysAllowed: false },
    },
    gstReportingCycles: {
        fetchByEntity: { path: '/gst-reporting-cycles/entity/{entityId}', method: 'GET', alwaysAllowed: false },
        save: { path: '/gst-reporting-cycles/entity/{entityId}', method: 'PUT', alwaysAllowed: false },
    },
    gstFieldMappings: {
        fetchByEntity: { path: '/gst-field-mappings/entity/{entityId}', method: 'GET', alwaysAllowed: false },
        save: { path: '/gst-field-mappings/entity/{entityId}', method: 'PUT', alwaysAllowed: false },
    },
    forecastGroups: {
        fetchByEntity: { path: '/forecast-groups/entity/{entityId}', method: 'GET', alwaysAllowed: false },
        createMajorHeading: { path: '/forecast-groups/major-heading', method: 'POST', alwaysAllowed: false },
        updateMajorHeading: { path: '/forecast-groups/major-heading/{id}', method: 'PUT', alwaysAllowed: false },
        deleteMajorHeading: { path: '/forecast-groups/major-heading/{id}', method: 'DELETE', alwaysAllowed: false },
        createSubHeading: { path: '/forecast-groups/major-heading/{majorHeadingId}/sub-heading', method: 'POST', alwaysAllowed: false },
        updateSubHeading: { path: '/forecast-groups/sub-heading/{id}', method: 'PUT', alwaysAllowed: false },
        deleteSubHeading: { path: '/forecast-groups/sub-heading/{id}', method: 'DELETE', alwaysAllowed: false },
        addSubGroups: { path: '/forecast-groups/major-heading/{majorHeadingId}/sub-groups', method: 'POST', alwaysAllowed: false },
        addSubGroupsToSubHeading: { path: '/forecast-groups/sub-heading/{subHeadingId}/sub-groups', method: 'POST', alwaysAllowed: false },
        updateSubGroup: { path: '/forecast-groups/sub-group/{id}', method: 'PUT', alwaysAllowed: false },
        deleteSubGroup: { path: '/forecast-groups/sub-group/{id}', method: 'DELETE', alwaysAllowed: false },
    },
    colorScheme: {
        fetch: { path: '/color-scheme', method: 'GET', alwaysAllowed: false },
        save: { path: '/color-scheme', method: 'PUT', alwaysAllowed: false },
    },
    provisionAging: {
        fetchStatus: { path: '/provision-aging/status/{entityId}', method: 'GET', alwaysAllowed: true },
        trigger: { path: '/provision-aging/trigger/{entityId}', method: 'POST', alwaysAllowed: true },
    },
    generalProvisions: {
        fetchByEntity: { path: '/general-provisions/entity/{entityId}', method: 'GET', alwaysAllowed: false },
        fetchTransactions: { path: '/general-provisions/entity/{entityId}/{kind}/transactions', method: 'GET', alwaysAllowed: false },
        update: { path: '/general-provisions/entity/{entityId}/{kind}', method: 'PUT', alwaysAllowed: false },
        setStatus: { path: '/general-provisions/entity/{entityId}/{kind}/status', method: 'PATCH', alwaysAllowed: false },
    },
    subscription: {
        fetchStatus: { path: '/subscription/entity/{entityId}', method: 'GET', alwaysAllowed: true },
        select: { path: '/subscription/entity/{entityId}/select', method: 'POST', alwaysAllowed: true },
        acknowledgeUpgrade: { path: '/subscription/entity/{entityId}/acknowledge', method: 'POST', alwaysAllowed: true },
        toggleAutoRenew: { path: '/subscription/entity/{entityId}/auto-renew', method: 'POST', alwaysAllowed: true },
        // A paid add-on on/off (express-server subscription-addons-service.js).
        setAddon: { path: '/subscription/entity/{entityId}/addons/{addonKey}', method: 'POST', alwaysAllowed: true },
        billingHistory: { path: '/subscription/entity/{entityId}/billing-history', method: 'GET', alwaysAllowed: true },
        // The plan prompt for a feature the plan doesn't have (read-only pages).
        featureGate: { path: '/subscription/entity/{entityId}/feature-gate/{feature}', method: 'GET', alwaysAllowed: true },
        adminSet: { path: '/subscription/entity/{entityId}/admin-set', method: 'POST', alwaysAllowed: false },
        stripeInitiate: { path: '/subscription/entity/{entityId}/stripe/initiate', method: 'POST', alwaysAllowed: true },
        stripeChangePlan: { path: '/subscription/entity/{entityId}/stripe/change-plan', method: 'POST', alwaysAllowed: true },
        stripeUpgradeForFeature: { path: '/subscription/entity/{entityId}/stripe/upgrade-for-feature', method: 'POST', alwaysAllowed: true },
        // Applies a just-confirmed payment without waiting for Stripe's webhook.
        stripeSync: { path: '/subscription/entity/{entityId}/stripe/sync', method: 'POST', alwaysAllowed: true },
        // A subscription payment that didn't go through, and paying it in-app.
        overdue: { path: '/subscription/entity/{entityId}/overdue', method: 'GET', alwaysAllowed: true },
        payOverdue: { path: '/subscription/entity/{entityId}/overdue/pay', method: 'POST', alwaysAllowed: true },
        // Secret Options: the QA Daily plan for this company.
        qaDailyPlan: { path: '/subscription/entity/{entityId}/qa-daily-plan', method: 'GET', alwaysAllowed: true },
        setQaDailyPlan: { path: '/subscription/entity/{entityId}/qa-daily-plan', method: 'POST', alwaysAllowed: true },
        platformStripeKey: { path: '/subscription/platform-stripe-key', method: 'GET', alwaysAllowed: true },
    },
    subscriptionTiers: {
        list: { path: '/subscription-tiers', method: 'GET', alwaysAllowed: true },
        update: { path: '/subscription-tiers/{id}', method: 'PATCH', alwaysAllowed: false },
        reset: { path: '/subscription-tiers/reset', method: 'POST', alwaysAllowed: false },
        // New plan (Monthly + Yearly tiers) / delete a tier nobody is on.
        create: { path: '/subscription-tiers', method: 'POST', alwaysAllowed: false },
        delete: { path: '/subscription-tiers/{id}', method: 'DELETE', alwaysAllowed: false },
        // The add-on catalog - Admin > Subscription Add-ons.
        addons: { path: '/subscription-tiers/addons', method: 'GET', alwaysAllowed: false },
        updateAddon: { path: '/subscription-tiers/addons/{key}', method: 'PATCH', alwaysAllowed: false },
        // Capacity add-ons (more users / a longer horizon) - created and deleted by an admin.
        createAddon: { path: '/subscription-tiers/addons', method: 'POST', alwaysAllowed: false },
        deleteAddon: { path: '/subscription-tiers/addons/{key}', method: 'DELETE', alwaysAllowed: false },
    },
    subscriptionReimbursements: {
        list: { path: '/subscription-reimbursements', method: 'GET', alwaysAllowed: false },
        transactions: { path: '/subscription-reimbursements/transactions', method: 'GET', alwaysAllowed: false },
        resolve: { path: '/subscription-reimbursements/{id}', method: 'PATCH', alwaysAllowed: false },
    },
    stripe: {
        get: {
            path: '/stripe/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        save: {
            path: '/stripe/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        remove: {
            path: '/stripe/{entityId}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        sync: {
            path: '/stripe/{entityId}/sync',
            method: 'POST',
            alwaysAllowed: false,
        },
        syncStatus: {
            path: '/stripe/{entityId}/sync/status',
            method: 'GET',
            alwaysAllowed: false,
        },
    },
    xero: {
        getIntegration: {
            path: '/xero/integration/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        connect: {
            path: '/xero/connect/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        refreshConnectLink: {
            path: '/xero/connect-link/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        disconnect: {
            path: '/xero/connection/{entityId}',
            method: 'DELETE',
            alwaysAllowed: false,
        },
        syncGL: {
            path: '/xero/sync/gl/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        syncTransactions: {
            path: '/xero/sync/bank-transactions/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        syncInvoices: {
            path: '/xero/sync/invoices/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        syncStale: {
            path: '/xero/sync/stale/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        syncStatuses: {
            path: '/xero/sync/statuses/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        syncClients: {
            path: '/xero/sync/clients/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        getStatus: {
            path: '/xero/status/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        syncHistory: {
            path: '/xero/sync-history/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        importStatus: {
            path: '/xero/import-status/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        // Synced invoices held back past the company's projection horizon.
        invoicesBeyondHorizon: {
            path: '/xero/invoices/beyond-horizon/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
        // Imports the held-back invoices once the horizon is extended.
        continueInvoiceImport: {
            path: '/xero/invoices/continue-import/{entityId}',
            method: 'POST',
            alwaysAllowed: false,
        },
        getGLAccounts: {
            path: '/xero/gl/{entityId}',
            method: 'GET',
            alwaysAllowed: false,
        },
    },
};

export default routes;
